import { useContext } from "react";
import { GridItem, Grid } from "@reverb-ui/react";
import VisitorsFunnel from "presentation/components/VisitorsFunnel";
import CardHighlights from "./CardHighlights";
import { HighlightsContext } from "../context-api";
import TopExperience from "presentation/components/TopExperience";
import { TopExperienceProvider } from "../context-api/TopExperienceProvider";
import { CardSkeleton } from "presentation/components/Skeleton/CardSkeleton";
import { VisitorsFunnelSkeleton } from "presentation/components/Skeleton/VisitorsFunnelSkeleton";
import { panelsInfo } from "helper/panelsHelper";
import PanelContainer from "presentation/components/PanelContainer";
import { ReportsFilterContext } from "../context-api/ReportsFilterProvider";

const cardGridTemplates = {
  comparission: {
    columns: "1fr 1fr",
    areas: `
      'card0 card3' 
      'card1 card2' 
      'card4 card5'
      'funnel funnel'
      'funnel funnel'
    `,
  },
  completeHighilights: {
    columns: "2.5fr 2.5fr 4fr 3fr",
    areas: `
      'card0 card3 funnel top5' 
      'card1 card2 funnel top5' 
      'card4 card4 card5 top5'
    `,
  },
  default: {
    columns: "2.5fr 2.5fr 5.5fr",
    areas: `
      'card0 card3 funnel' 
      'card1 card2 funnel' 
      'card4 card5 funnel'
    `,
  },
};

const getCardGridTemplateColumns = (
  completeHighilights: boolean,
  comparisonParams: boolean
): string => {
  if (comparisonParams) {
    return cardGridTemplates.comparission.columns;
  }

  return completeHighilights
    ? cardGridTemplates.completeHighilights.columns
    : cardGridTemplates.default.columns;
};

const getCardGridTemplateAreas = (
  completeHighilights: boolean,
  comparisonParams: boolean
): string => {
  if (comparisonParams) {
    return cardGridTemplates.comparission.areas;
  }

  return completeHighilights
    ? cardGridTemplates.completeHighilights.areas
    : cardGridTemplates.default.areas;
};

const highlightsInfo = panelsInfo.highlights;
const minHComparissionCard = "154px";

export const Highlights = () => {
  const {
    requestParamsIndex,
    highlightsData,
    reportsDatasource,
    downloadHighlightsCSV,
    downloadingReports,
    showCompleteHighlights,
    isLoading,
  } = useContext(HighlightsContext);
  const { comparisonParams } = useContext(ReportsFilterContext);

  return (
    <PanelContainer
      panelId={highlightsInfo.htmlId}
      panelName={highlightsInfo.description}
      rangeName={highlightsData?.range}
      isLoading={isLoading}
      downloadOptions={{
        onClickGenerateCSV: downloadHighlightsCSV,
        downloadingReports: downloadingReports,
        panelInfo: highlightsInfo,
        filterStateIndex: requestParamsIndex,
      }}
    >
      <Grid
        py={2}
        gap={3}
        gridTemplateColumns={getCardGridTemplateColumns(
          showCompleteHighlights,
          comparisonParams
        )}
        templateAreas={getCardGridTemplateAreas(
          showCompleteHighlights,
          comparisonParams
        )}
      >
        {isLoading
          ? Array(6)
              .fill(0)
              .map((item, index) => (
                <GridItem gridArea={`card${index}`} key={index}>
                  <CardSkeleton {...item} hasVariationPeriod={true} />
                </GridItem>
              ))
          : highlightsData?.cards?.map((item, index) => (
              <GridItem gridArea={`card${index}`} key={index}>
                <CardHighlights
                  minH={comparisonParams ? minHComparissionCard : undefined}
                  {...item}
                />
              </GridItem>
            ))}

        <GridItem gridArea="funnel">
          {isLoading ? (
            <VisitorsFunnelSkeleton />
          ) : (
            <VisitorsFunnel visitors={highlightsData?.visitorsFunnel} />
          )}
        </GridItem>

        {showCompleteHighlights && !comparisonParams && (
          <GridItem gridArea="top5">
            {reportsDatasource && (
              <TopExperienceProvider
                requestParamsIndex={requestParamsIndex}
                reportsDatasource={reportsDatasource}
              >
                <TopExperience />
              </TopExperienceProvider>
            )}
          </GridItem>
        )}
      </Grid>
    </PanelContainer>
  );
};
